import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M06InputCustomerScreen'>;

export const M06InputCustomerScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [step, setStep] = useState(1);

  // Step 1 State: Personal info
  const [fullName, setFullName] = useState('Nguyễn Văn Minh');
  const [dob, setDob] = useState('18/06/1991');
  const [gender, setGender] = useState<'Nam' | 'Nữ'>('Nam');
  const [occupation, setOccupation] = useState('Nhân viên văn phòng');
  const [income, setIncome] = useState('30.000.000');
  const [goal, setGoal] = useState<string[]>(['Bảo vệ gia đình', 'Tích lũy cho con']);

  // Step 2 State: Policy config
  const [sumAssured, setSumAssured] = useState('1.000.000.000');
  const [term, setTerm] = useState('20 năm');
  const [freq, setFreq] = useState('Hàng năm');
  const [estPremium, setEstPremium] = useState('30.000.000');

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      // Navigate to M07ResultScreen
      navigation.navigate('M07ResultScreen', { illustrationId: 'mock-id-123' });
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigation.goBack();
    }
  };

  const handleToggleGoal = (g: string) => {
    if (goal.includes(g)) {
      setGoal(goal.filter((item) => item !== g));
    } else {
      setGoal([...goal, g]);
    }
  };

  const applyAISuggestion = () => {
    setSumAssured('1.500.000.000');
    setTerm('15 năm');
    setEstPremium('50.000.000');
  };

  return (
    <View style={styles.container}>
      {/* Step Indicator */}
      <View style={styles.stepHeader}>
        <Text style={styles.stepText}>Bước {step}/3</Text>
        <View style={styles.indicatorRow}>
          <View style={[styles.indicatorLine, step >= 1 && styles.indicatorActive]} />
          <View style={[styles.indicatorLine, step >= 2 && styles.indicatorActive]} />
          <View style={[styles.indicatorLine, step >= 3 && styles.indicatorActive]} />
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {step === 1 && (
          <View style={styles.stepContent}>
            <Text style={styles.sectionTitle}>📋 Thông tin người được bảo hiểm</Text>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Họ và tên *</Text>
              <TextInput style={styles.input} value={fullName} onChangeText={setFullName} />
            </View>

            <View style={styles.row}>
              <View style={[styles.inputContainer, { flex: 1 }]}>
                <Text style={styles.label}>Ngày sinh *</Text>
                <TextInput style={styles.input} placeholder="DD/MM/YYYY" value={dob} onChangeText={setDob} />
              </View>
              <View style={[styles.inputContainer, { width: 120 }]}>
                <Text style={styles.label}>Giới tính *</Text>
                <View style={styles.genderRow}>
                  <TouchableOpacity
                    style={[styles.genderBtn, gender === 'Nam' && styles.genderActive]}
                    onPress={() => setGender('Nam')}
                  >
                    <Text style={[styles.genderText, gender === 'Nam' && styles.genderTextActive]}>Nam</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.genderBtn, gender === 'Nữ' && styles.genderActive]}
                    onPress={() => setGender('Nữ')}
                  >
                    <Text style={[styles.genderText, gender === 'Nữ' && styles.genderTextActive]}>Nữ</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Nghề nghiệp</Text>
              <TextInput style={styles.input} value={occupation} onChangeText={setOccupation} />
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Thu nhập hàng tháng (VND)</Text>
              <TextInput style={styles.input} keyboardType="numeric" value={income} onChangeText={setIncome} />
            </View>

            <Text style={styles.label}>Mục tiêu tài chính</Text>
            <View style={styles.goalGrid}>
              {['Bảo vệ gia đình', 'Tích lũy cho con', 'Hưu trí', 'Đầu tư sinh lời'].map((g) => {
                const active = goal.includes(g);
                return (
                  <TouchableOpacity
                    key={g}
                    style={[styles.goalItem, active && styles.goalActive]}
                    onPress={() => handleToggleGoal(g)}
                  >
                    <Icon name={active ? 'check_circle' : 'circle'} size={16} color={active ? colors.secondary : '#9e9e9e'} />
                    <Text style={[styles.goalText, active && styles.goalTextActive]}>{g}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        {step === 2 && (
          <View style={styles.stepContent}>
            <Text style={styles.productBadge}>ManulifeMOVE - {fullName} ({dob})</Text>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>💰 Số tiền bảo hiểm *</Text>
              <TextInput style={styles.input} value={sumAssured} onChangeText={setSumAssured} />
              <View style={styles.chipRow}>
                {['500tr', '1 tỷ', '2 tỷ', '3 tỷ'].map((c) => (
                  <TouchableOpacity
                    key={c}
                    style={styles.chip}
                    onPress={() => setSumAssured(c === '500tr' ? '500.000.000' : c === '1 tỷ' ? '1.000.000.000' : c === '2 tỷ' ? '2.000.000.000' : '3.000.000.000')}
                  >
                    <Text style={styles.chipText}>{c}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>📅 Thời hạn đóng phí *</Text>
              <View style={styles.genderRow}>
                {['10 năm', '20 năm', 'Trọn đời'].map((t) => (
                  <TouchableOpacity
                    key={t}
                    style={[styles.genderBtn, term === t && styles.genderActive, { flex: 1 }]}
                    onPress={() => setTerm(t)}
                  >
                    <Text style={[styles.genderText, term === t && styles.genderTextActive]}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>💵 Phương thức đóng phí *</Text>
              <TextInput style={styles.input} value={freq} onChangeText={setFreq} />
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>💰 Phí đóng hàng năm ước tính</Text>
              <TextInput style={styles.input} value={estPremium} onChangeText={setEstPremium} />
            </View>

            {/* AI suggestion box */}
            <View style={styles.aiCard}>
              <View style={styles.aiHeader}>
                <Icon name="calculate" size={20} color={colors.primary} />
                <Text style={styles.aiTitle}>Gợi ý từ AI Copilot</Text>
              </View>
              <Text style={styles.aiContent}>
                Với thu nhập {income}đ/tháng, phí {estPremium}đ/năm là an toàn. Tuy nhiên, 
                để đạt mục tiêu "Tích lũy cho con", bạn có thể cân nhắc tăng phí lên 50tr/năm và rút ngắn thời hạn đóng phí xuống 15 năm để nhận lãi suất kép tối ưu.
              </Text>
              <TouchableOpacity style={styles.applyBtn} onPress={applyAISuggestion}>
                <Text style={styles.applyBtnText}>Áp dụng gợi ý</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {step === 3 && (
          <View style={styles.stepContent}>
            <Text style={styles.sectionTitle}>✅ Đã sẵn sàng tính toán</Text>
            <View style={styles.summaryCard}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Khách hàng:</Text>
                <Text style={styles.summaryValue}>{fullName} ({gender})</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Ngày sinh:</Text>
                <Text style={styles.summaryValue}>{dob}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Sản phẩm chính:</Text>
                <Text style={styles.summaryValue}>ManulifeMOVE</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Số tiền bảo hiểm:</Text>
                <Text style={styles.summaryValue}>{sumAssured} VNĐ</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Thời hạn đóng phí:</Text>
                <Text style={styles.summaryValue}>{term}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Phí đóng hàng năm:</Text>
                <Text style={styles.summaryValue}>{estPremium} VNĐ</Text>
              </View>
            </View>

            <TouchableOpacity style={styles.editBtn} onPress={() => setStep(1)}>
              <Icon name="edit" size={16} color={colors.primary} />
              <Text style={styles.editBtnText}>Sửa thông tin</Text>
            </TouchableOpacity>

            <View style={styles.advancedOptions}>
              <Text style={styles.advancedTitle}>💡 Tính năng bổ trợ</Text>
              <View style={styles.optionRow}>
                <Icon name="check_circle" size={18} color={colors.secondary} />
                <Text style={styles.optionText}>Tự động so sánh nhiều phương án thời hạn</Text>
              </View>
              <View style={styles.optionRow}>
                <Icon name="check_circle" size={18} color={colors.secondary} />
                <Text style={styles.optionText}>Tự động sinh bài phân tích AI chi tiết</Text>
              </View>
            </View>
          </View>
        )}
      </ScrollView>

      {/* Navigation bottom bar */}
      <View style={styles.bottomBar}>
        <TouchableOpacity style={styles.backBtn} onPress={handleBack}>
          <Text style={styles.backBtnText}>Quay lại</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.nextBtn} onPress={handleNext}>
          <Text style={styles.nextBtnText}>{step === 3 ? 'XEM KẾT QUẢ' : 'TIẾP TỤC'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  stepHeader: {
    backgroundColor: '#ffffff',
    padding: spacing[3],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  stepText: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
    marginBottom: spacing[2],
  },
  indicatorRow: {
    flexDirection: 'row',
    gap: spacing[2],
  },
  indicatorLine: {
    flex: 1,
    height: 4,
    backgroundColor: '#e1e2e9',
    borderRadius: 2,
  },
  indicatorActive: {
    backgroundColor: colors.secondary,
  },
  scrollContent: {
    padding: spacing[4],
    paddingBottom: 88,
  },
  stepContent: {
    gap: spacing[4],
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[2],
  },
  productBadge: {
    alignSelf: 'flex-start',
    backgroundColor: colors.primaryFixed,
    color: colors.primary,
    fontSize: 12,
    fontWeight: fontWeights.bold,
    paddingHorizontal: spacing[2],
    paddingVertical: spacing[1],
    borderRadius: borderRadius.sm,
    marginBottom: spacing[2],
  },
  inputContainer: {
    gap: spacing[1.5],
  },
  label: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  input: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[3],
    height: 44,
    fontSize: 14,
    color: colors.onSurface,
  },
  row: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  genderRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    height: 44,
  },
  genderBtn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  genderActive: {
    backgroundColor: colors.primary,
  },
  genderText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  genderTextActive: {
    color: '#ffffff',
  },
  goalGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
  },
  goalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2],
    borderRadius: borderRadius.xl,
    gap: spacing[1.5],
  },
  goalActive: {
    borderColor: colors.secondary,
    backgroundColor: 'rgba(0,166,81,0.04)',
  },
  goalText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  goalTextActive: {
    color: colors.secondary,
    fontWeight: fontWeights.semibold,
  },
  chipRow: {
    flexDirection: 'row',
    gap: spacing[2],
    marginTop: spacing[1],
  },
  chip: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1.5],
  },
  chipText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  aiCard: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.md,
    padding: spacing[4],
    gap: spacing[2],
    marginTop: spacing[2],
  },
  aiHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  aiTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  aiContent: {
    fontSize: 12.5,
    lineHeight: 18,
    color: colors.onSurface,
    textAlign: 'justify',
  },
  applyBtn: {
    alignSelf: 'flex-end',
    backgroundColor: 'rgba(0,51,102,0.06)',
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1.5],
    borderRadius: borderRadius.sm,
  },
  applyBtnText: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  summaryCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[2.5],
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  summaryLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  summaryValue: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  editBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 40,
    gap: spacing[1.5],
    marginTop: spacing[2],
  },
  editBtnText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  advancedOptions: {
    backgroundColor: 'rgba(0,166,81,0.04)',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    gap: spacing[2],
    marginTop: spacing[2],
    borderWidth: 1,
    borderColor: 'rgba(0,166,81,0.1)',
  },
  advancedTitle: {
    fontSize: 13.5,
    fontWeight: fontWeights.bold,
    color: colors.secondary,
    marginBottom: spacing[1],
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  optionText: {
    fontSize: 12.5,
    color: colors.onSurface,
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    flexDirection: 'row',
    padding: spacing[4],
    borderTopWidth: 1,
    borderTopColor: colors.outlineVariant,
    gap: spacing[3],
  },
  backBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  nextBtn: {
    flex: 2,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nextBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },
});
